import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import { useTranslation } from "react-i18next";
import { AttributeTabsCustom  } from "./AttributeTabs.style";
import { TabsSkeleton } from "./TabsSkeleton.component";
import { FILTERS } from "../../consts";
import { useUserAttributes, useUserProgressWithAttributes/*, useAttributesQuery*/ } from "../../queries";
import { useAttributeTabStore } from "../../stores/attributeTabStore";


type IFilterTabs = {
  children?: React.ReactNode;
  onClickHandler: (value: string) => void;
}

export type Attribute = {
  attributeId: number|string
  corporateId: number
  attribute: string
  userDeleted: boolean
  creationDate: string
  updateDate: string
  forYou: boolean
  exploreCarousel: boolean
  deleted: boolean
}

type NewAttribute = {
    attributeName: string,
    attributeId: number|string,
    los: number,
    completedLos: number,
};
export const AttributeTabs = ({ onClickHandler, children }: IFilterTabs) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const attributes = useUserAttributes();
  const { setSelectedTab, selectedTab, resetAttribute } = useAttributeTabStore();
  const { isFetching, isError } = useUserProgressWithAttributes();
  //const { data: enrollmentAttributes } = useAttributesQuery(); // TODO: abilitare quando ci sarà possibilità di filtrare per i soli attributi per cui l'utente ha LO

  let attributesWithDefault: NewAttribute[];

  // Se c'è un errore nel caricamento degli attributi o non ci sono attributi, mostra solo i tab essenziali
  if (isError || !attributes || attributes.length === 0) {
    attributesWithDefault = [
      {
        attributeId: "total",
        attributeName: t("filters_all"),
        los: 0,
        completedLos: 0
      },
      {
        attributeId: "mandatory",
        attributeName: t("mandatory"),
        los: 0,
        completedLos: 0
      }
    ];
  } else {
    // Logica normale quando non c'è errore
    const filterAttributes = attributes?.filter(attr => attr.los > 0);

    // Prendi il primo attributo dell'array originale solo se ha contenuti
    const firstOriginalAttribute = attributes?.length > 0 && attributes[0].los > 0 ? attributes[0] : null;

    // Ottieni gli altri attributi filtrati (escluso sempre il primo dell'array originale)
    const otherFilteredAttributes = filterAttributes?.filter(attr =>
      attr.attributeId !== attributes?.[0]?.attributeId
    ) || [];

    // Costruisci l'array con l'ordine corretto:
    // Se primo attributo ha contenuti: "Tutti", "Primo attributo", "Obbligatoria", "Altri"
    // Se primo attributo non ha contenuti: "Tutti", "Obbligatoria", "Altri"
    attributesWithDefault = [
      // Sempre presente: tab "Tutti"
      {
        attributeId: "total",
        attributeName: t("filters_all"),
        los: 0,
        completedLos: 0
      },
      // Presente solo se ha contenuti: il primo attributo dell'array originale
      ...(firstOriginalAttribute ? [firstOriginalAttribute] : []),
      // Sempre presente: tab "Obbligatoria"
      {
        attributeId: "mandatory",
        attributeName: t("MANDATORY_ATTRIBUTE"),
        los: 0,
        completedLos: 0
      },
      // Aggiungi tutti gli altri attributi che hanno contenuti (escluso il primo)
      ...otherFilteredAttributes
    ];
  }


  const handleChange = (filter: NewAttribute) => {
    const value = filter.attributeId.toString();
    const widgetFilter = filter.attributeId.toString();
    onClickHandler(value);

    if (value === FILTERS.ALL) {
      resetAttribute();
      return;
    }
    setSelectedTab(widgetFilter);

  };

  const a11yProps = (id: string) => {
    return {
      id: `filter-tab-${id}`,
      "aria-controls": `filter-tabpanel-${id}`
    };
  };

  // Se stiamo ancora caricando e non ci sono attributi, mostra skeleton
  if(isFetching && (!attributesWithDefault || attributesWithDefault.length === 0)) {
    return <TabsSkeleton />;
  }

  // Se non ci sono attributi da mostrare (neanche quelli essenziali), mostra solo i children
  if(!attributesWithDefault || attributesWithDefault.length === 0) {
    return <div style={ { marginTop:"0.5rem" } }>{ children }</div>;
  }

  return (
    <>
      <AttributeTabsCustom
        sx={ {
          ".MuiTab-textColorPrimary:not(.Mui-selected)": { color:  theme.customColors.systemPrimary01 },
          ".Mui-selected": { color: theme.customColors.accentVariantA },
          fontFamily: "Inter"
        } }
        TabIndicatorProps={ { style: { background: theme.customColors.accentVariantA, borderRadius: "1.5rem" } } }
        variant="scrollable"
        scrollButtons={ true }
        allowScrollButtonsMobile
        value={ selectedTab.toString() }
        aria-label="filter tabs for you page">
        { (attributesWithDefault||[]).length > 1 ? attributesWithDefault.map((filter: NewAttribute, index) => (
          <Tab
            sx={ {
              textTransform: "none",
              fontSize: "0.875rem",
              letterSpacing: "0.0313em",
              fontWeight: 600,
              fontStyle: "semibold",
              height: "1.5rem",
              minWidth: "3rem",
              marginX:"0.35rem",
              lineHeight: "1.5rem",
              borderBottom: `2px solid ${theme.customColors.border}`,
              "&.Mui-focusVisible": {
                border: `1px solid ${theme.customColors.accentVariantA}`
              }
            } }
            key={ index }
            label={ filter.attributeName }
            value={ filter.attributeId.toString() }
            onClick={ () => handleChange(filter) }
            { ...a11yProps(filter.attributeId.toString()) }
          />
        )):null }
      </AttributeTabsCustom>
      { (attributesWithDefault||[]).map((filter: NewAttribute) => (
        <div
          tabIndex={ -1 }
          key={ filter.attributeId.toString() }
          role="tabpanel"
          hidden={ filter.attributeId.toString() !== selectedTab }
          id={ `filter-tabpanel-${filter.attributeId.toString()}` }
          aria-labelledby={ `filter-tab-${filter.attributeId.toString()}` }
        >
          { selectedTab === filter.attributeId.toString() && <Box>{ children }</Box> }
        </div>
      ))
      }
    </>
  );
};
